import UserService from '@apis/UserService';
import { useAuth } from '@contexts/AuthContext';
import { useEffect, useRef } from 'react';

/**
 * Best-effort capture of the user's browser timezone.
 *
 * Leo's date/day and scheduling anchors render in the user's wall clock
 * (#4185/#4186); the backend resolves that zone from a canonical
 * `timezone` field on the profile. This component supplies it from the only
 * place that reliably knows the user's zone on web — their browser
 * (`Intl…timeZone`).
 *
 * Discipline (per the global-component auth rule): reads the cached
 * `currentUser` and never fetches `/me`, so it can't trigger a refresh loop on
 * a logged-out/expired session. Fires a single fire-and-forget settings write,
 * only when the stored zone differs from the browser's, and skips masquerade so
 * an operator's zone is never stamped onto an impersonated user.
 */
const TimezoneCapture = () => {
  const { currentUser, isMasquerading } = useAuth();
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    if (!currentUser || isMasquerading) return;

    let browserTz = '';
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      browserTz = '';
    }
    if (!browserTz || currentUser.timezone === browserTz) return;

    sentRef.current = true;
    UserService.updateTimezone(browserTz).catch(() => {
      // Fire-and-forget: the resolver falls back to the canonical default if
      // this never lands. Allow a retry on the next mount.
      sentRef.current = false;
    });
  }, [currentUser, isMasquerading]);

  return null;
};

export default TimezoneCapture;
